import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Container,
    ListGroup,
    Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import notificationService from '@/shared/services/notificationService';
import './NotificationsPage.css';
import useMyCandidacies from '@/shared/hooks/useMyCandidacies';
import StatusPill from '@/components/common/StatusPill';
import {
    denverDayAfter,
    denverDayOf,
    formatAnswerDay,
} from '@/shared/utils/deadline';

const PAGE_SIZE = 20;

const PROGRAM_LABELS = { si: 'SI', ta: 'TA', la: 'LA' };

// Paths whose routes are only registered while a flag is on. A notice written
// before the flag went off would otherwise link to a blank page.
const FLAGGED_PREFIXES = [
    ['/training', 'training'],
    ['/surveys', 'surveys'],
    ['/apply', 'studentApplication'],
    ['/student-applications', 'studentApplication'],
    ['/programs', 'coordinatorReview'],
];

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
    timeStyle: 'short',
    timeZone: 'America/Denver',
});

/**
 * Where a notification opens: its stored link when that is a path inside the
 * app, else the application it is about, else nowhere. Anything that is not
 * an in-app path (a full URL, `javascript:`) is never followed.
 */
export function targetOf(notification, enabledFlags = {}) {
    const { link, relatedEntity } = notification;
    if (
        relatedEntity?.entityType === 'Candidacy' &&
        relatedEntity.entityId &&
        link === '/applications'
    ) {
        return `/applications?position=${relatedEntity.entityId}`;
    }
    if (
        typeof link === 'string' &&
        link.startsWith('/') &&
        !link.startsWith('//')
    ) {
        const gated = FLAGGED_PREFIXES.find(
            ([prefix]) => link === prefix || link.startsWith(`${prefix}/`)
        );
        if (gated && !enabledFlags[gated[1]]) return null;
        return link;
    }
    if (relatedEntity?.entityType === 'Application' && relatedEntity.entityId) {
        return `/applications/${relatedEntity.entityId}`;
    }
    return null;
}

const OFFER_EVENTS = ['offer.made', 'offer.reminder'];

// What an offer notice reads now, by where the position stands. A stage the
// student cannot act on is said as a result, never as the call to action the
// notice was when it arrived.
const OFFER_STATE_TEXT = {
    accepted: 'You accepted this offer.',
    declined: 'You declined this offer.',
    expired: 'This offer expired.',
    released: 'You left this position.',
    withdrawn: 'You withdrew.',
};

/**
 * The position a notice is about, when it is about one the student holds a
 * candidacy for: its current stage and, for an open offer, the answer-by day.
 */
export function stateOf(notification, candidacies) {
    const { relatedEntity, event } = notification;
    if (relatedEntity?.entityType !== 'Candidacy') return null;
    const candidacy = candidacies.find((c) => c.id === relatedEntity.entityId);
    if (!candidacy) return null;
    let text = null;
    if (OFFER_EVENTS.includes(event)) {
        text =
            candidacy.stage === 'offered'
                ? `Waiting for your answer${
                      candidacy.offer?.respondBy
                          ? `, by ${formatAnswerDay(candidacy.offer.respondBy)}`
                          : ''
                  }.`
                : OFFER_STATE_TEXT[candidacy.stage] || null;
    }
    return { stage: candidacy.stage, text };
}

const dayKey = (value) =>
    Number.isNaN(new Date(value).getTime()) ? '' : denverDayOf(value);

/** "Today", "Yesterday", or "Wed, Oct 21" for the Denver day a notice arrived. */
export function dayHeading(value, now = new Date()) {
    const key = dayKey(value);
    if (!key) return 'Earlier';
    if (key === denverDayOf(now)) return 'Today';
    if (key === denverDayAfter(-1, now)) return 'Yesterday';
    return new Date(value).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Denver',
    });
}

function groupByDay(items) {
    const groups = [];
    for (const item of items) {
        const key = dayKey(item.createdAt);
        const last = groups[groups.length - 1];
        if (last && last.key === key) last.items.push(item);
        else groups.push({ key, items: [item] });
    }
    return groups;
}

function formatTime(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : TIME_FORMAT.format(date);
}

function NotificationsPage() {
    const enabledFlags = {
        training: useFeature('training'),
        surveys: useFeature('surveys'),
        studentApplication: useFeature('studentApplication'),
        coordinatorReview: useFeature('coordinatorReview'),
    };

    const [items, setItems] = useState([]);
    // Paging follows the server's list, not ours: de-duplication can leave
    // `items` shorter than what the server has handed back, so neither the
    // next offset nor "is there more" may be derived from items.length.
    const [nextOffset, setNextOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [loadingMore, setLoadingMore] = useState(false);
    const [actionError, setActionError] = useState('');
    const [status, setStatus] = useState('');
    const [focusId, setFocusId] = useState(null);
    // The positions behind the offer and interview notices, read only when
    // one is on the page
    const aboutPositions = items.some(
        (item) => item.relatedEntity?.entityType === 'Candidacy'
    );
    const { candidacies } = useMyCandidacies({ skip: !aboutPositions });

    const actionErrorRef = useRevealAlert(actionError);
    const headingRef = useRef(null);
    // The element each item hands focus to: its link, or its title when it
    // has nowhere to go. A control that disappears (Mark read, Mark all read,
    // Load more) moves focus here instead of dropping it on the page body.
    const itemRefs = useRef(new Map());

    const load = useCallback(() => {
        let active = true;
        setLoading(true);
        setLoadError('');
        notificationService
            .getMyNotifications({ limit: PAGE_SIZE, offset: 0 })
            .then((data) => {
                if (!active) return;
                const page = data.notifications || [];
                setItems(page);
                setNextOffset(page.length);
                setHasMore(page.length > 0 && page.length < (data.total || 0));
                setUnreadCount(data.unreadCount || 0);
            })
            .catch(() => {
                if (active) {
                    setLoadError('We could not load your notifications.');
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => load(), [load]);

    useEffect(() => {
        if (!focusId) return;
        if (focusId === 'heading') {
            headingRef.current?.focus();
        } else {
            itemRefs.current.get(focusId)?.focus();
        }
        setFocusId(null);
    }, [focusId]);

    const registerItem = (id) => (element) => {
        if (element) {
            itemRefs.current.set(id, element);
        } else {
            itemRefs.current.delete(id);
        }
    };

    const markRead = async (notification) => {
        setActionError('');
        setStatus('');
        try {
            await notificationService.markAsRead(notification._id);
            setItems((prev) =>
                prev.map((item) =>
                    item._id === notification._id
                        ? { ...item, read: true }
                        : item
                )
            );
            setUnreadCount((count) => Math.max(0, count - 1));
            setStatus(`Marked "${notification.title}" as read.`);
            setFocusId(notification._id);
        } catch (_error) {
            setActionError(
                'We could not mark that notification as read. Please try again.'
            );
        }
    };

    const markAllRead = async () => {
        setActionError('');
        setStatus('');
        try {
            await notificationService.markAllAsRead();
            setItems((prev) => prev.map((item) => ({ ...item, read: true })));
            setUnreadCount(0);
            setStatus('All notifications marked as read.');
            setFocusId('heading');
        } catch (_error) {
            setActionError(
                'We could not mark your notifications as read. Please try again.'
            );
        }
    };

    const loadMore = async () => {
        setActionError('');
        setLoadingMore(true);
        try {
            const data = await notificationService.getMyNotifications({
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            const page = data.notifications || [];
            // A notice that arrived since the first page shifts the offsets,
            // so the next page can repeat one already shown.
            const seen = new Set(items.map((item) => item._id));
            const fresh = page.filter((item) => !seen.has(item._id));
            const consumed = nextOffset + page.length;
            const more = page.length > 0 && consumed < (data.total || 0);
            setItems((prev) => [...prev, ...fresh]);
            setNextOffset(consumed);
            setHasMore(more);
            setUnreadCount(data.unreadCount || 0);
            if (fresh.length > 0) {
                setFocusId(fresh[0]._id);
            } else if (!more && items.length > 0) {
                // Nothing new and the button is gone: keep focus in the list
                setFocusId(items[items.length - 1]._id);
            }
        } catch (_error) {
            setActionError('We could not load more notifications.');
        } finally {
            setLoadingMore(false);
        }
    };

    // Opening a notice reads it. Not awaited, so the navigation is never held
    // up; the bell hears about it through the service either way.
    const openNotification = (notification) => {
        if (!notification.read) {
            notificationService
                .markAsRead(notification._id)
                .catch(() => undefined);
        }
    };

    const renderItem = (notification) => {
        const target = targetOf(notification, enabledFlags);
        const unread = !notification.read;
        const state = stateOf(notification, candidacies);
        return (
            <ListGroup.Item
                as="li"
                key={notification._id}
                className="d-flex align-items-start gap-3"
            >
                <span
                    aria-hidden="true"
                    className={`flex-shrink-0 rounded-circle mt-2 notification-dot ${
                        unread ? 'bg-primary' : ''
                    }`}
                />
                <div className="flex-grow-1">
                    <h3
                        className={`h6 mb-1 ${unread ? 'fw-bold' : 'fw-normal'}`}
                    >
                        {unread && (
                            <span className="visually-hidden">Unread </span>
                        )}
                        {target ? (
                            <Link
                                to={target}
                                ref={registerItem(notification._id)}
                                onClick={() => openNotification(notification)}
                            >
                                {notification.title}
                            </Link>
                        ) : (
                            <span
                                ref={registerItem(notification._id)}
                                tabIndex={-1}
                            >
                                {notification.title}
                            </span>
                        )}
                        {PROGRAM_LABELS[notification.program] && (
                            <Badge bg="secondary" className="ms-2">
                                {PROGRAM_LABELS[notification.program]}
                            </Badge>
                        )}
                    </h3>
                    <p className="mb-1">{notification.message}</p>
                    {state && (
                        <p className="mb-1">
                            <StatusPill
                                family="candidacy"
                                value={state.stage}
                            />
                            {state.text && (
                                <span className="ms-2 fw-semibold">
                                    {state.text}
                                </span>
                            )}
                        </p>
                    )}
                    <time
                        dateTime={notification.createdAt}
                        className="small text-muted"
                    >
                        {formatTime(notification.createdAt)}
                    </time>
                </div>
                {unread && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="flex-shrink-0"
                        aria-label={`Mark read: ${notification.title}`}
                        onClick={() => markRead(notification)}
                    >
                        Mark read
                    </Button>
                )}
            </ListGroup.Item>
        );
    };

    if (loading) {
        return (
            <Container className="py-4">
                <h1>Notifications</h1>
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">
                            Loading notifications...
                        </span>
                    </Spinner>
                </div>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                    <h1 ref={headingRef} tabIndex={-1} className="mb-1">
                        Notifications
                    </h1>
                    {!loadError && (
                        <p className="text-muted mb-0">
                            {unreadCount === 0
                                ? 'You have no unread notifications.'
                                : `${unreadCount} unread`}
                        </p>
                    )}
                </div>
                {!loadError && unreadCount > 0 && (
                    <Button variant="outline-primary" onClick={markAllRead}>
                        Mark all read
                    </Button>
                )}
            </div>

            {/* Speaks only after the person acts, never on load */}
            <div role="status" className="visually-hidden">
                {status}
            </div>

            {actionError && (
                <Alert variant="danger" ref={actionErrorRef} tabIndex={-1}>
                    {actionError}
                </Alert>
            )}

            {loadError ? (
                <Alert variant="danger">
                    <p>{loadError}</p>
                    <Button variant="outline-danger" onClick={load}>
                        Try again
                    </Button>
                </Alert>
            ) : items.length === 0 ? (
                <p>You have no notifications.</p>
            ) : (
                <>
                    {groupByDay(items).map((group) => (
                        <section
                            key={group.key}
                            aria-labelledby={`day-${group.key.replace(/\s/g, '-')}`}
                            className="mb-4"
                        >
                            <h2
                                id={`day-${group.key.replace(/\s/g, '-')}`}
                                className="h6 text-body-secondary text-uppercase mb-2"
                            >
                                {dayHeading(group.items[0].createdAt)}
                            </h2>
                            <ListGroup as="ul">
                                {group.items.map((notification) =>
                                    renderItem(notification)
                                )}
                            </ListGroup>
                        </section>
                    ))}
                    {hasMore && (
                        <div className="text-center mt-3">
                            <Button
                                variant="outline-primary"
                                onClick={loadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore
                                    ? 'Loading...'
                                    : 'Load more notifications'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </Container>
    );
}

export default NotificationsPage;
